import { supabase } from './supabase';
import { StockItem } from './stockItems';
import { Customer } from './customers';

export interface OrderItem {
  id?: string;
  order_id?: string;
  item_id: string;
  quantity: number;
  received_quantity?: number;
  unit_price: number;
  total_price: number;
  item_name: string;
  item_unit: string;
  status?: 'Not Received' | 'Partially Received' | 'Received';
  notes?: string;
  created_at?: string;
}

export interface Order {
  id?: string;
  business_id: string;
  supplier_id: string;
  supplier?: Customer;
  order_number: string;
  total_amount: number;
  status: 'pending' | 'received' | 'cancelled';
  created_at?: string;
  updated_at?: string;
  received_at?: string;
  cancelled_at?: string;
  notes?: string;
  items?: OrderItem[];
}

// Create a new order
export async function createOrder(order: Order, orderItems: OrderItem[]) {
  if (!supabase) throw new Error('Database connection not available');
  
  try {
    // Start a transaction by opening a single connection
    const { data: orderData, error: orderError } = await supabase
      .from('orders')
      .insert({
        business_id: order.business_id,
        supplier_id: order.supplier_id,
        order_number: order.order_number,
        total_amount: order.total_amount,
        status: order.status,
        notes: order.notes
      })
      .select()
      .single();
    
    if (orderError) throw orderError;
    if (!orderData) throw new Error('Failed to create order');
    
    // Prepare order items
    const itemsToInsert = orderItems.map(item => ({
      order_id: orderData.id,
      item_id: item.item_id,
      quantity: item.quantity,
      received_quantity: 0,
      unit_price: item.unit_price,
      total_price: item.total_price,
      item_name: item.item_name,
      item_unit: item.item_unit,
      status: 'Not Received',
      notes: item.notes
    }));
    
    // Insert order items
    const { error: itemsError } = await supabase
      .from('order_items')
      .insert(itemsToInsert);
    
    if (itemsError) throw itemsError;
    
    return { ...orderData, items: orderItems };
    
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
}

// Get orders for a business
export async function getOrders(businessId: string) {
  if (!supabase) throw new Error('Database connection not available');
  
  try {
    // Get all orders for the business
    const { data: orders, error: ordersError } = await supabase
      .from('orders')
      .select(`
        *,
        supplier:customers(*)
      `)
      .eq('business_id', businessId)
      .order('created_at', { ascending: false });
    
    if (ordersError) throw ordersError;
    
    // For each order, get the order items
    const ordersWithItems = await Promise.all(
      orders.map(async (order) => {
        if (!supabase) throw new Error('Database connection not available');
        
        const { data: items, error: itemsError } = await supabase
          .from('order_items')
          .select('*')
          .eq('order_id', order.id)
          .order('created_at');
        
        if (itemsError) throw itemsError;
        
        return {
          ...order,
          items: items
        };
      })
    );
    
    return ordersWithItems;
    
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw error;
  }
}

// Update order status
export async function updateOrderStatus(
  orderId: string, 
  status: 'pending' | 'received' | 'cancelled'
) {
  if (!supabase) throw new Error('Database connection not available');
  
  try {
    // If cancelling, check if any items have been received
    if (status === 'cancelled') {
      const { data: items, error: itemsError } = await supabase
        .from('order_items')
        .select('status')
        .eq('order_id', orderId);
      
      if (itemsError) throw itemsError;
      
      // If any items are marked as received, prevent cancellation
      const hasReceivedItems = items.some(item => item.status === 'Received');
      if (hasReceivedItems) {
        throw new Error('Cannot cancel order with received items');
      }
    }
    
    // Update the order status
    const { data, error } = await supabase
      .from('orders')
      .update({ 
        status,
        ...(status === 'received' && { received_at: new Date().toISOString() }),
        updated_at: new Date().toISOString()
      })
      .eq('id', orderId)
      .select()
      .single();
    
    if (error) throw error;
    
    // Note: Inventory updates are handled individually by updateOrderItemStatus function
    // when items are marked as received, so we don't duplicate that logic here
    
    return data;
    
  } catch (error) {
    console.error('Error updating order status:', error);
    throw error;
  }
}

// Update individual order item status
export async function updateOrderItemStatus(
  itemId: string, 
  status: 'Not Received' | 'Partially Received' | 'Received',
  receivedQuantity?: number
) {
  if (!supabase) throw new Error('Database connection not available');
  
  try {
    // Get the order item first
    const { data: item, error: itemError } = await supabase
      .from('order_items')
      .select('*')
      .eq('id', itemId)
      .single();
    
    if (itemError) throw itemError;
    if (!item) throw new Error('Order item not found');
    
    // Calculate received quantity
    let newReceivedQuantity = item.received_quantity || 0;
    
    if (status === 'Received') {
      // If fully received, set received quantity to total quantity
      newReceivedQuantity = item.quantity;
    } else if (status === 'Partially Received' && receivedQuantity !== undefined) {
      // For partial receipt, add the new received quantity
      newReceivedQuantity = Math.min(item.quantity, receivedQuantity);
    } else if (status === 'Not Received') {
      // Reset received quantity
      newReceivedQuantity = 0;
    }
    
    // Determine correct status based on quantities
    let effectiveStatus = status;
    if (newReceivedQuantity === 0) {
      effectiveStatus = 'Not Received';
    } else if (newReceivedQuantity < item.quantity) {
      effectiveStatus = 'Partially Received';
    } else {
      effectiveStatus = 'Received';
    }
    
    // Update the status and received quantity
    const { data, error } = await supabase
      .from('order_items')
      .update({ 
        status: effectiveStatus,
        received_quantity: newReceivedQuantity
      })
      .eq('id', itemId)
      .select()
      .single();
    
    if (error) throw error;
    
    // Calculate how many new units were received in this update
    const additionalQuantity = newReceivedQuantity - (item.received_quantity || 0);
    
    // Only update inventory if we received additional items
    if (additionalQuantity > 0) {
      // Create stock transaction - the database trigger will automatically update stock quantities
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select('order_number')
        .eq('id', item.order_id)
        .single();
      
      if (orderError) {
        console.error(`Error fetching order ${item.order_id}:`, orderError);
        throw orderError;
      }
      
      const transactionNote = `Received ${additionalQuantity} ${item.item_unit}${additionalQuantity > 1 ? 's' : ''} from Order #${orderData.order_number}`;
      
      const { error: transactionError } = await supabase
        .from('stock_transactions')
        .insert({
          item_id: item.item_id,
          type: 'purchase',
          quantity: additionalQuantity, // Only log the newly received quantity
          price: item.unit_price,
          notes: transactionNote
        });
      
      if (transactionError) {
        console.error('Error creating transaction:', transactionError);
        throw transactionError;
      }
      
      // Check if all items in the order are received
      if (!supabase) throw new Error('Database connection not available');
      
      const { data: orderItems, error: itemsError } = await supabase
        .from('order_items')
        .select('*')
        .eq('order_id', item.order_id);
      
      if (itemsError) throw itemsError;
      
      const allItemsReceived = orderItems.every(item => item.status === 'Received');
      
      // If all items are received, update the order status
      if (allItemsReceived) {
        const { error: orderUpdateError } = await supabase
          .from('orders')
          .update({ 
            status: 'received',
            received_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', item.order_id);
        
        if (orderUpdateError) {
          console.error(`Error updating order status ${item.order_id}:`, orderUpdateError);
          throw orderUpdateError;
        }
      }
    }
    
    return data;
    
  } catch (error) {
    console.error('Error updating order item status:', error);
    throw error;
  }
}

// Mark all items in an order as received
export async function markAllItemsAsReceived(orderId: string) {
  if (!supabase) throw new Error('Database connection not available');
  
  try {
    // First get all items for the order
    const { data: items, error: itemsError } = await supabase
      .from('order_items')
      .select('*')
      .eq('order_id', orderId)
      .not('status', 'eq', 'Received'); // Get items that aren't fully received yet
    
    if (itemsError) throw itemsError;
    if (!items || items.length === 0) return []; // No items to update
    
    // Process each item individually to properly update inventory
    const updatedItems = await Promise.all(
      items.map(async (item) => {
        if (!supabase) throw new Error('Database connection not available');
        
        // Calculate how many new units to receive
        const newUnitsToReceive = item.quantity - (item.received_quantity || 0);
        
        // Skip if no new units to receive
        if (newUnitsToReceive <= 0) return item;
        
        // Update the item status
        const { data: updatedItem, error: updateError } = await supabase
          .from('order_items')
          .update({ 
            status: 'Received',
            received_quantity: item.quantity // Set received quantity to full quantity
          })
          .eq('id', item.id)
          .select()
          .single();
        
        if (updateError) {
          console.error(`Error updating order item ${item.id}:`, updateError);
          throw updateError;
        }
        
        // Create stock transaction - the database trigger will automatically update stock quantities
        // Get the order for reference
        const { data: orderData, error: orderError } = await supabase
          .from('orders')
          .select('order_number')
          .eq('id', orderId)
          .single();
        
        if (orderError) {
          console.error(`Error fetching order ${orderId}:`, orderError);
          throw orderError;
        }
        
        const transactionNote = `Received ${newUnitsToReceive} ${item.item_unit}${newUnitsToReceive > 1 ? 's' : ''} from Order #${orderData.order_number}`;
        
        const { error: transactionError } = await supabase
          .from('stock_transactions')
          .insert({
            item_id: item.item_id,
            type: 'purchase',
            quantity: newUnitsToReceive,
            price: item.unit_price,
            notes: transactionNote
          });
        
        if (transactionError) {
          console.error('Error creating transaction:', transactionError);
          throw transactionError;
        }
        
        return updatedItem;
      })
    );
    
    // Update the order status to received
    const { data, error } = await supabase
      .from('orders')
      .update({ 
        status: 'received',
        received_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', orderId)
      .select()
      .single();
    
    if (error) throw error;
    
    return updatedItems.filter(Boolean); // Filter out any potential null values
    
  } catch (error) {
    console.error('Error marking all items as received:', error);
    throw error;
  }
}

// Delete an order
export async function deleteOrder(orderId: string) {
  if (!supabase) throw new Error('Database connection not available');
  
  try {
    // Check if any items have been received
    const { data: items, error: itemsError } = await supabase
      .from('order_items')
      .select('status')
      .eq('order_id', orderId);
    
    if (itemsError) throw itemsError;
    
    // If any items are marked as received, prevent deletion
    const hasReceivedItems = items.some(item => item.status === 'Received');
    if (hasReceivedItems) {
      throw new Error('Cannot delete order with received items');
    }
    
    // First delete all order items
    const { error: deleteItemsError } = await supabase
      .from('order_items')
      .delete()
      .eq('order_id', orderId);
    
    if (deleteItemsError) throw deleteItemsError;
    
    // Then delete the order
    const { error: deleteOrderError } = await supabase
      .from('orders')
      .delete()
      .eq('id', orderId);
    
    if (deleteOrderError) throw deleteOrderError;
    
    return { success: true };
    
  } catch (error) {
    console.error('Error deleting order:', error);
    throw error;
  }
}

// Get items with pending orders
export async function getPendingOrdersForItems(businessId: string) {
  if (!supabase) throw new Error('Database connection not available');
  
  try {
    // First get all pending orders
    const { data: pendingOrders, error: ordersError } = await supabase
      .from('orders')
      .select('id')
      .eq('business_id', businessId)
      .eq('status', 'pending');
    
    if (ordersError) throw ordersError;
    
    if (!pendingOrders || pendingOrders.length === 0) {
      return []; // No pending orders, return empty array
    }
    
    // Get all order items for the pending orders
    const orderIds = pendingOrders.map(order => order.id);
    
    const { data: orderItems, error: itemsError } = await supabase
      .from('order_items')
      .select('item_id, quantity')
      .in('order_id', orderIds);
    
    if (itemsError) throw itemsError;
    
    // Create a map of item IDs to quantities ordered
    const itemMap = new Map<string, number>();
    
    orderItems.forEach(item => {
      const currentQuantity = itemMap.get(item.item_id) || 0;
      itemMap.set(item.item_id, currentQuantity + item.quantity);
    });
    
    // Convert to array and return
    return Array.from(itemMap.entries()).map(([itemId, quantityOrdered]) => ({
      itemId,
      quantityOrdered
    }));
    
  } catch (error) {
    console.error('Error fetching pending orders for items:', error);
    throw error;
  }
} 